import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, MenuItem, Stack, FormControlLabel, Switch,
} from '@mui/material';
import {useState} from 'react';
import type {
    BillingPeriod, IapProduct, IapProductCreateReq,
    IapProductUpdateReq, ProductType, Store,
} from '../../types/iap';

interface Props {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: IapProductCreateReq | IapProductUpdateReq) => Promise<void>;
    initial?: IapProduct;
}

export function IapProductFormDialog({open, onClose, onSubmit, initial}: Props) {
    const isEdit = !!initial;
    const [form, setForm] = useState({
        store:        (initial?.store        ?? 'GOOGLE')       as Store,
        productType:  (initial?.productType  ?? 'SUBSCRIPTION') as ProductType,
        internalKey:  initial?.internalKey  ?? '',
        displayName:  initial?.displayName  ?? '',
        priceKrw:     initial?.priceKrw     ?? 0,
        pointAmount:  (initial?.pointAmount  ?? '') as number | '',
        billingPeriod: (initial?.billingPeriod ?? 'MONTHLY') as BillingPeriod,
        productId:    initial?.productId    ?? '',
        basePlanId:   initial?.basePlanId   ?? '',
        isActive:     initial?.isActive     ?? true,
    });
    const [loading, setLoading] = useState(false);

    const showBasePlanId = form.store === 'GOOGLE' && form.productType === 'SUBSCRIPTION';

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const common = {
                displayName: form.displayName,
                priceKrw:    Number(form.priceKrw),
                ...(form.productType === 'CONSUMABLE' && form.pointAmount !== '' && {pointAmount: Number(form.pointAmount)}),
                ...(form.productType === 'SUBSCRIPTION' && {billingPeriod: form.billingPeriod}),
                ...(form.productId   && {productId:  form.productId}),
                ...(showBasePlanId   && form.basePlanId && {basePlanId: form.basePlanId}),
            };
            const data: IapProductCreateReq | IapProductUpdateReq = isEdit
                ? {...common, isActive: form.isActive} as IapProductUpdateReq
                : {...common, store: form.store, productType: form.productType, internalKey: form.internalKey} as IapProductCreateReq;
            await onSubmit(data);
            onClose();
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? '상품 수정' : '상품 등록'}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    {!isEdit && (
                        <>
                            <TextField
                                select label="스토어"
                                value={form.store}
                                onChange={e => setForm(f => ({...f, store: e.target.value as Store}))}
                                fullWidth
                            >
                                <MenuItem value="GOOGLE">Google Play</MenuItem>
                                <MenuItem value="APPLE">App Store</MenuItem>
                            </TextField>
                            <TextField
                                select label="상품 타입"
                                value={form.productType}
                                onChange={e => setForm(f => ({...f, productType: e.target.value as ProductType}))}
                                fullWidth
                            >
                                <MenuItem value="SUBSCRIPTION">구독</MenuItem>
                                <MenuItem value="CONSUMABLE">포인트 (소비재)</MenuItem>
                            </TextField>
                            <TextField
                                label="내부 키 (예: plus_monthly)"
                                value={form.internalKey}
                                onChange={e => setForm(f => ({...f, internalKey: e.target.value}))}
                                fullWidth
                                helperText="영문, 숫자, _ 만 사용 (수정 불가)"
                            />
                        </>
                    )}
                    <TextField
                        label="상품명"
                        value={form.displayName}
                        onChange={e => setForm(f => ({...f, displayName: e.target.value}))}
                        fullWidth
                    />
                    <TextField
                        label="원화 가격 (₩)" type="number"
                        value={form.priceKrw}
                        onChange={e => setForm(f => ({...f, priceKrw: e.target.value as unknown as number}))}
                        fullWidth
                    />
                    {form.productType === 'CONSUMABLE' && (
                        <TextField
                            label="포인트 수량 (P)" type="number"
                            value={form.pointAmount}
                            onChange={e => setForm(f => ({...f, pointAmount: e.target.value as unknown as number}))}
                            fullWidth
                        />
                    )}
                    {form.productType === 'SUBSCRIPTION' && (
                        <TextField
                            select label="과금 주기"
                            value={form.billingPeriod}
                            onChange={e => setForm(f => ({...f, billingPeriod: e.target.value as BillingPeriod}))}
                            fullWidth
                        >
                            <MenuItem value="MONTHLY">월간</MenuItem>
                            <MenuItem value="YEARLY">연간</MenuItem>
                        </TextField>
                    )}
                    <TextField
                        label={form.store === 'GOOGLE' ? 'Play Store 상품 ID' : 'App Store 상품 ID'}
                        value={form.productId}
                        onChange={e => setForm(f => ({...f, productId: e.target.value}))}
                        fullWidth
                    />
                    {showBasePlanId && (
                        <TextField
                            label="Base Plan ID (Android 구독)"
                            value={form.basePlanId}
                            onChange={e => setForm(f => ({...f, basePlanId: e.target.value}))}
                            fullWidth
                            helperText="예: monthly-v1"
                        />
                    )}
                    {isEdit && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.isActive}
                                    onChange={e => setForm(f => ({...f, isActive: e.target.checked}))}
                                />
                            }
                            label="활성화"
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>취소</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                    {loading ? '처리 중...' : isEdit ? '수정' : '등록'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
